/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../../theme/theme"
import { css, Typography, Box, Switch, FormControlLabel } from "@mui/material"
import { Add } from "@mui/icons-material"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { mobileCss } from "../../../util/isMobile"
import { useQuestionnaireForm } from "../context/QuestionnaireFormContext"
import { ScheduleEvent } from "../../common/context/SharedFormTypes"
import { ScheduleEventDialog } from "./components/ScheduleEventDialog"
import { ScheduleEventCard } from "./components/ScheduleEventCard"
import { MyButton } from "../../../components/button/MyButton"

const StepTitleStyle = withMyTheme((theme) => css`
    font-size: 2rem;
    font-weight: 600;
    color: ${theme.palette.primary.main};
    text-align: center;
    margin-bottom: 1rem;

    ${mobileCss(`
        font-size: 1.5rem;
    `)}
`)

const StepContentStyle = withMyTheme(() => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5vh;
    text-align: center;
    margin: 0 auto;
    padding: 0 1rem;

    ${mobileCss(`
        max-width: 90vw;
        gap: 1.5rem;
    `)}
`)

const ToggleContainerStyle = withMyTheme(() => css`
    display: flex;
    justify-content: center;
    margin-bottom: 0.5rem;
`)

const ToggleLabelStyle = withMyTheme((theme) => css`
    .MuiFormControlLabel-label {
        font-family: ${theme.typography.body1.fontFamily};
        font-size: 1.1rem;
        font-weight: 500;
        color: ${theme.palette.text.primary};
    }
`)

const SwitchStyle = withMyTheme((theme) => css`
    & .MuiSwitch-switchBase.Mui-checked {
        color: ${theme.palette.primary.main};
    }
    & .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
        background-color: ${theme.palette.primary.main};
    }
`)

const StayTunedToggleStyle = withMyTheme((theme) => css`
    .MuiFormControlLabel-label {
        font-family: ${theme.typography.body1.fontFamily};
        font-size: 0.95rem;
        color: ${theme.palette.text.secondary};
    }
`)

const EventsContainerStyle = withMyTheme(() => css`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 50vw;
    margin-top: 0.5rem;

    ${mobileCss(`
        width: 85vw;
    `)}
`)

const AddButtonWrapperStyle = withMyTheme(() => css`
    display: flex;
    justify-content: center;
    margin-top: 0.5rem;
`)

const StayTunedDescStyle = withMyTheme((theme) => css`
    font-size: 0.85rem;
    color: ${theme.palette.text.secondary};
    font-style: italic;
    max-width: 400px;
`)

interface ScheduleStepProps {
    onStart?: () => void
}

export const ScheduleStep = ({}: ScheduleStepProps) => {
    const { t } = useTranslation()
    const { formData, updateSchedule } = useQuestionnaireForm()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [editingEvent, setEditingEvent] = useState<ScheduleEvent | null>(null)

    const schedule = formData.schedule ?? { enabled: false, stayTuned: false, events: [] }
    const primaryColor = formData.colors?.primaryColor || '#C45526'
    const isEnabled = schedule.enabled !== false

    const handleEnableToggle = () => {
        updateSchedule({ enabled: !isEnabled })
    }

    const handleStayTunedToggle = () => {
        updateSchedule({ stayTuned: !schedule.stayTuned })
    }

    const handleAddEvent = () => {
        setEditingEvent(null)
        setDialogOpen(true)
    }

    const handleEditEvent = (event: ScheduleEvent) => {
        setEditingEvent(event)
        setDialogOpen(true)
    }

    const handleSaveEvent = (event: ScheduleEvent) => {
        const existing = schedule.events.find(e => e.id === event.id)
        const updatedEvents = existing
            ? schedule.events.map(e => e.id === event.id ? event : e)
            : [...schedule.events, event]
        updateSchedule({ events: updatedEvents })
    }

    const handleDeleteEvent = (id: string) => {
        updateSchedule({ events: schedule.events.filter(e => e.id !== id) })
    }

    return (
        <div css={StepContentStyle}>
            <Typography css={StepTitleStyle}>
                {t('questionnaire.schedule.title')}
            </Typography>

            <Box css={ToggleContainerStyle}>
                <FormControlLabel
                    css={ToggleLabelStyle}
                    control={
                        <Switch
                            css={SwitchStyle}
                            checked={isEnabled}
                            onChange={handleEnableToggle}
                        />
                    }
                    label={t('questionnaire.schedule.enableToggle')}
                />
            </Box>

            {isEnabled && (
                <>
                    <Box css={ToggleContainerStyle}>
                        <FormControlLabel
                            css={StayTunedToggleStyle}
                            control={
                                <Switch
                                    css={SwitchStyle}
                                    checked={schedule.stayTuned}
                                    onChange={handleStayTunedToggle}
                                    size="small"
                                />
                            }
                            label={t('questionnaire.schedule.stayTunedToggle')}
                        />
                    </Box>
                    {schedule.stayTuned && (
                        <Typography css={StayTunedDescStyle}>
                            {t('questionnaire.schedule.stayTunedDescription')}
                        </Typography>
                    )}

                    <Box css={EventsContainerStyle}>
                        {schedule.events.map(event => (
                            <ScheduleEventCard
                                key={event.id}
                                event={event}
                                primaryColor={primaryColor}
                                onClick={() => handleEditEvent(event)}
                                onDelete={() => handleDeleteEvent(event.id)}
                            />
                        ))}
                    </Box>

                    <Box css={AddButtonWrapperStyle}>
                        <MyButton
                            variant="primary"
                            text={t('questionnaire.schedule.addEvent')}
                            startIcon={<Add />}
                            onClick={handleAddEvent}
                        />
                    </Box>

                    <ScheduleEventDialog
                        open={dialogOpen}
                        event={editingEvent}
                        primaryColor={primaryColor}
                        onSave={handleSaveEvent}
                        onClose={() => setDialogOpen(false)}
                    />
                </>
            )}
        </div>
    )
}
